import { StageDef } from '../automationMachineInterfaces'
import { REDDIT_AUTOMATION_STAGE_STATUSES } from './redditAutomationStageStatuses'
import * as redditUrls from './redditUrls'
import { getSwitchValue } from './switchUtils'
import { RedditSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { _debug } from '../../utils/logging'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getOptionalElement } from '../utils'

type TwoFactorAuthenticationSetting = {
  key: RedditSettingKey
  section: string
  label: string
  oldLabel: string
}

const settings: TwoFactorAuthenticationSetting[] = [
  {
    key: RedditSettingKey.TWO_FACTOR_AUTHENTICATION,
    section: 'settings-account-section',
    label: 'Two-factor authentication',
    oldLabel: 'Use two-factor authentication',
  },
]

const getCurrentValueFn = (key: string) =>
  getCurrentValueFnFromKey(key, (value) => (value === true ? 'On' : 'Off'))

const getIsFulfilledFn = (key: string) =>
  getIsFulfilledFnFromKey(key, (value) => value === true)

async function get2faSettingValue(): Promise<boolean | null> {
  const settingToggleElement = await getOptionalElement<HTMLInputElement>(
    document,
    `//faceplate-switch-input[@aria-label="Two-factor authentication"]`,
  )

  if (!settingToggleElement) {
    _debug(
      'Reddit 2FA Gather failed on finding settings toggle',
      'automation stage machine',
      'd',
    )
    return null
  }

  const isEnabled = settingToggleElement.ariaChecked === 'true'

  _debug(
    `Reddit 2FA Gather found ${isEnabled}`,
    'automation stage machine',
    'd',
  )

  return isEnabled
}

const gatherStageDefs: StageDef[] = [
  // Start off by navigating to the homepage before gathering the 2FA setting,
  // because the "Use two-factor authentication" switch isn't changed in the UI
  // after enabling or disabling 2FA. This messes up the gather unless the page
  // is reloaded first.
  {
    type: enums.StageType.ACTION,
    key: `INIT_REDDIT_SETTING_${RedditSettingKey.TWO_FACTOR_AUTHENTICATION}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    action: async () => {
      window.location.assign(redditUrls.homeUrl)
    },
  },
  ...settings.flatMap((s) => [
    // Reddit appears to be migrating users to a version of settings page where
    // 2FA is with account settings
    {
      type: enums.StageType.ACTION,
      key: `GATHER_REDDIT_SETTING_NEW_PAGE_${s.key}`,
      status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
      transitionNextStageMap: {
        [enums.TriggeringEventType.ON_EVENT_ACTION_COMPLETED]:
          `GATHER_REDDIT_SETTING_FINISH_${s.key}`,
        [enums.TriggeringEventType.ON_ERROR]:
          `GATHER_REDDIT_SETTING_OLD_PAGE_${s.key}`,
      },
      url: redditUrls.accountSettingsUrl,
      action: async (_machineContext, userContext) => {
        const value = await get2faSettingValue()

        if (value !== null) {
          await userContext.updateStoredInitialValues(
            {
              [s.key]: value,
            },
            enums.RedditRecommendationKey.REDDIT_2FA,
          )
        } else {
          throw new Error('Could not find 2FA setting')
        }
      },
    },
    // Some users still have old settings, where 2fa lives on privacy page
    {
      type: enums.StageType.ACTION,
      key: `GATHER_REDDIT_SETTING_OLD_PAGE_${s.key}`,
      status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
      url: redditUrls.privacySettingsUrl,
      action: async (_machineContext, userContext) => {
        const value = await getSwitchValue(s.section, s.label, s.oldLabel)
        await userContext.updateStoredInitialValues(
          {
            [s.key]: value,
          },
          enums.RedditRecommendationKey.REDDIT_2FA,
        )
      },
    },
    // Placeholder to transition to after successfully gathering setting
    {
      type: enums.StageType.ACTION,
      key: `GATHER_REDDIT_SETTING_FINISH_${s.key}`,
      status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
      action: async () => {},
    },
  ]),
]

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = []

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
